/* ===================================================================
   Google Posts -- compose an update, offer, event or alert, schedule it,
   and manage the queue. WIRED to /api/v1/gbp/* (2026-08-05).

   This one PUBLISHES. Unlike the social lane (dry-run until Meta App
   Review), GBP API access is approved and needs no new scope, so a
   scheduled post really does go live on the customer's profile at its
   time. The UI says so plainly rather than hiding it.

   STYLING: every class is namespaced .gbp__* and defined in
   styles/gbp-posts.css off the token layer, so light / dark / outdoor
   all resolve from one set of rules. The first cut of this screen reused
   class names from Social.tsx that did not exist, so it rendered with no
   styles at all -- white-on-white text and default browser widgets. Do
   not reintroduce a class here without a rule to back it.

   ICP: a tradesperson on a phone between jobs, often in glare. Plain
   labels ("Update", "Call now"), big tap targets, and every error is a
   sentence rather than a code.
   =================================================================== */
import { useMemo, useRef, useState } from 'react';
import { Icon } from '../components/ui';
import {
  useGbpPosts,
  useGbpPostMutations,
  useGbpImageMutations,
  useGbpImageRequirements,
  sortGbpPosts,
} from '../hooks/useGbpPosts';
import {
  GBP_CAPTION_MAX,
  GBP_CTA_LABELS,
  GBP_POST_TYPE_LABELS,
  gbpImageMessage,
  validateGbpPost,
  type GbpCtaType,
  type GbpImage,
  type GbpPost,
  type GbpPostType,
} from '../lib/api';
import '../styles/gbp-posts.css';

const POST_TYPES: GbpPostType[] = ['standard', 'offer', 'event', 'alert'];
const CTA_TYPES: GbpCtaType[] = [
  'BOOK', 'ORDER', 'SHOP', 'LEARN_MORE', 'SIGN_UP', 'CALL',
];

function fmtWhen(iso: string | null): string {
  if (!iso) return 'Not scheduled';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

/* Plain-language status. "publishing" is transient and would read as a stuck
   state, so it becomes "Posting now...". */
function statusLabel(p: GbpPost): string {
  switch (p.status) {
    case 'posted': return `Posted ${fmtWhen(p.posted_at)}`;
    case 'publishing': return 'Posting now...';
    case 'scheduled': return `Goes out ${fmtWhen(p.scheduled_at)}`;
    case 'failed': return "Didn't post";
    default: return 'Draft';
  }
}

const ExternalIcon = <path d="M14 3h7v7M21 3l-9 9M10 5H5v14h14v-5" />;

/* Fallback if /gbp/images/requirements hasn't answered yet. The server is the
   real gate (magic bytes + re-encode); this only spares the owner an obviously
   pointless round trip on a wrong file. 10 MB matches MAX_UPLOAD_BYTES. */
const FALLBACK_MAX_MB = 10;

export function GbpPosts() {
  const { data, isLoading, error } = useGbpPosts();
  const { create, update, remove, publishNow } = useGbpPostMutations();
  const { upload: uploadImage, remove: removeImage } = useGbpImageMutations();
  const { data: imageReqs } = useGbpImageRequirements();

  const [postType, setPostType] = useState<GbpPostType>('standard');
  const [caption, setCaption] = useState('');
  const [ctaType, setCtaType] = useState<GbpCtaType | ''>('');
  const [ctaUrl, setCtaUrl] = useState('');
  const [when, setWhen] = useState('');
  const [eventTitle, setEventTitle] = useState('');
  const [eventStart, setEventStart] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  // Image: the just-picked preview shows instantly while the upload runs; once
  // it lands, `image` holds the stored library image whose url rides in the
  // post's design.photo_urls. imageError is its own line so a photo problem
  // never gets tangled up with the post-text validation message.
  const fileRef = useRef<HTMLInputElement>(null);
  const [image, setImage] = useState<GbpImage | null>(null);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);

  const posts = useMemo(() => sortGbpPosts(data?.posts || []), [data]);
  const needsEvent = postType === 'event' || postType === 'offer';
  const maxMb = imageReqs?.max_upload_mb ?? FALLBACK_MAX_MB;
  const formatsLabel = (imageReqs?.formats ?? ['JPG', 'PNG']).join(' or ');
  const uploading = uploadImage.isPending;
  const shownImage = localPreview ?? image?.url ?? null;

  function buildInput(scheduled: boolean) {
    // The image url rides in the shared `design` jsonb alongside any event
    // details; the publish worker filters photo_urls through usable_photo_urls
    // before handing them to Google, so an absent image is simply a text post.
    const design: Record<string, unknown> = {};
    if (needsEvent) {
      design.event = {
        title: eventTitle.trim(),
        schedule: eventStart ? { startDate: eventStart } : undefined,
      };
    }
    if (image?.url) design.photo_urls = [image.url];
    return {
      caption,
      post_type: postType,
      cta_type: ctaType || null,
      cta_url: ctaType && ctaType !== 'CALL' ? ctaUrl.trim() : null,
      scheduled_at: scheduled && when ? new Date(when).toISOString() : null,
      design: Object.keys(design).length ? design : undefined,
    };
  }

  function pickImage() {
    setImageError(null);
    fileRef.current?.click();
  }

  function clearImageInput() {
    setImage(null);
    setLocalPreview(null);
    setImageError(null);
  }

  /* Remove a picked photo. If it already uploaded to the library, delete it too
     so an abandoned pick doesn't sit in the (small) rotating pool. Best effort:
     the row is gone from the composer either way. This is ONLY safe before the
     post is created; after create the post owns the url and we never touch it. */
  function removePickedImage() {
    const id = image?.id;
    clearImageInput();
    if (id) removeImage.mutate(id);
  }

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // let the owner re-pick the same file
    if (!file) return;
    setImageError(null);
    if (!file.type.startsWith('image/')) {
      setImageError(
        `That file isn't a photo. Please choose a ${formatsLabel} photo from your phone.`,
      );
      return;
    }
    if (file.size > maxMb * 1024 * 1024) {
      setImageError(
        `That photo is larger than ${maxMb} MB. A photo straight from your phone is normally well under that.`,
      );
      return;
    }
    // If replacing an already-uploaded image, drop the old one from the library.
    const prevId = image?.id;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      setLocalPreview(dataUrl);
      setImage(null);
      uploadImage.mutate(
        { dataUrl },
        {
          onSuccess: (res) => {
            setImage(res.image);
            setLocalPreview(null);
            if (prevId) removeImage.mutate(prevId);
          },
          onError: (err) => {
            setLocalPreview(null);
            setImageError(
              gbpImageMessage(
                err,
                "That photo couldn't be added just now. Please try again.",
              ),
            );
          },
        },
      );
    };
    reader.onerror = () =>
      setImageError("We couldn't read that file. Please try another photo.");
    reader.readAsDataURL(file);
  }

  function reset() {
    setCaption(''); setCtaType(''); setCtaUrl(''); setWhen('');
    setEventTitle(''); setEventStart(''); setFormError(null);
    // Clear the composer's reference WITHOUT deleting: the created post now owns
    // this image's url (Google fetches it at publish time).
    clearImageInput();
  }

  async function submit(scheduled: boolean) {
    const input = buildInput(scheduled);
    // Check before the round trip so the owner sees the problem while typing.
    const problem = validateGbpPost(input);
    if (problem) { setFormError(problem); return; }
    if (scheduled && !when) {
      setFormError('Pick a date and time, or save it as a draft.');
      return;
    }
    setFormError(null);
    try {
      await create.mutateAsync(input);
      reset();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setFormError(
        msg.includes('google_not_connected')
          ? 'Connect your Google Business Profile first, then you can schedule posts.'
          : msg,
      );
    }
  }

  const over = caption.length > GBP_CAPTION_MAX;

  return (
    <div className="gbp">
      <header className="gbp__head">
        <h1>Google posts</h1>
        <p>
          Updates, offers and events that show on your Google Business Profile.
          Anything you schedule goes out on its own.
        </p>
      </header>

      <div className="gbp__grid">
        {/* ---------------- Compose ----------------
            GREEN, not blue (re-mapped 2026-08-06 for coherence with the nav).
            Google Posts lives in the GROW section, and GROW is green. Blue
            here said "outbound" -- true of the composer in isolation, but it
            made the one screen in Grow that had a hue contradict its own
            section label. The whole screen is about your live Google profile,
            which is exactly what green means: DELIVERED / CONNECTED. */}
        <section className="gbp__card hue-green sec-topcap">
          <h2>Write a post</h2>

          <div className="gbp__seg" role="group" aria-label="Post type">
            {POST_TYPES.map((t) => (
              <button
                key={t}
                type="button"
                aria-pressed={postType === t}
                className={`gbp__segbtn${postType === t ? ' is-on' : ''}`}
                onClick={() => setPostType(t)}
              >
                {GBP_POST_TYPE_LABELS[t]}
              </button>
            ))}
          </div>

          {needsEvent && (
            <div className="gbp__row" style={{ marginTop: 16 }}>
              <label className="gbp__field" style={{ marginTop: 0 }}>
                <span className="gbp__label">
                  {postType === 'offer' ? 'Offer name' : 'Event name'}
                </span>
                <input
                  className="gbp__input"
                  value={eventTitle}
                  onChange={(e) => setEventTitle(e.target.value)}
                  placeholder={postType === 'offer' ? '10% off spring service' : 'Open house'}
                />
              </label>
              <label className="gbp__field" style={{ marginTop: 0 }}>
                <span className="gbp__label">Starts</span>
                <input
                  className="gbp__input"
                  type="date"
                  value={eventStart}
                  onChange={(e) => setEventStart(e.target.value)}
                />
              </label>
            </div>
          )}

          <label className="gbp__field">
            <span className="gbp__label">What it says</span>
            <textarea
              className="gbp__textarea"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="We're booking spring tune-ups now. Same-day service most weeks."
            />
            {/* Only surface the counter once it's actually close to mattering. */}
            {caption.length > GBP_CAPTION_MAX - 200 && (
              <span className={`gbp__count${over ? ' is-over' : ''}`}>
                {caption.length} / {GBP_CAPTION_MAX}
              </span>
            )}
          </label>

          {/* ---------------- Photo (optional) ----------------
              A post with a photo gets far more attention on Google. The file
              input itself is visually hidden; a big, bold tap target drives it
              so it works for a tradesperson on a phone in glare. On mobile,
              accept="image/*" with no `capture` lets the OS offer Camera OR
              Library. The upload validates + re-encodes + strips EXIF/GPS
              server-side, so any phone JPG/PNG is fine. */}
          <div className="gbp__field">
            <span className="gbp__label">Photo (optional)</span>
            <input
              ref={fileRef}
              className="gbp__file"
              type="file"
              accept="image/jpeg,image/png,image/*"
              onChange={onFile}
              aria-hidden="true"
              tabIndex={-1}
            />
            {shownImage ? (
              <div className="gbp__photo">
                <div className="gbp__photowrap">
                  <img
                    className="gbp__photoimg"
                    src={shownImage}
                    alt="Photo for this post"
                  />
                  {uploading && (
                    <div className="gbp__photoover" role="status">
                      <span className="gbp__spinner" aria-hidden="true" />
                      Adding photo...
                    </div>
                  )}
                </div>
                <div className="gbp__photoactions">
                  <button
                    type="button"
                    className="gbp__btn"
                    onClick={pickImage}
                    disabled={uploading}
                  >
                    Replace photo
                  </button>
                  <button
                    type="button"
                    className="gbp__btn gbp__btn--danger"
                    onClick={removePickedImage}
                    disabled={uploading}
                  >
                    Remove photo
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="gbp__photodrop"
                onClick={pickImage}
                disabled={uploading}
              >
                <span className="gbp__photoico" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2}>
                    <rect x="3" y="5" width="18" height="14" rx="3" />
                    <circle cx="12" cy="12" r="3.2" />
                    <path d="M8 5l1.4-2h5.2L16 5" strokeLinejoin="round" />
                  </svg>
                </span>
                <span className="gbp__photolab">Add a photo</span>
                <span className="gbp__photosub">
                  {formatsLabel}, up to {maxMb} MB. Tap to use your camera or pick one.
                </span>
              </button>
            )}
            {imageError && (
              <p className="gbp__error" role="alert">{imageError}</p>
            )}
          </div>

          <div className="gbp__row">
            <label className="gbp__field" style={{ marginTop: 0 }}>
              <span className="gbp__label">Button (optional)</span>
              <select
                className="gbp__select"
                value={ctaType}
                onChange={(e) => setCtaType(e.target.value as GbpCtaType | '')}
              >
                <option value="">No button</option>
                {CTA_TYPES.map((c) => (
                  <option key={c} value={c}>{GBP_CTA_LABELS[c]}</option>
                ))}
              </select>
            </label>
            {ctaType && ctaType !== 'CALL' && (
              <label className="gbp__field" style={{ marginTop: 0 }}>
                <span className="gbp__label">Button link</span>
                <input
                  className="gbp__input"
                  type="url"
                  value={ctaUrl}
                  onChange={(e) => setCtaUrl(e.target.value)}
                  placeholder="https://"
                />
              </label>
            )}
          </div>
          {ctaType === 'CALL' && (
            <span className="gbp__hint">
              Uses the phone number on your Google profile.
            </span>
          )}

          <label className="gbp__field">
            <span className="gbp__label">When</span>
            <input
              className="gbp__input"
              type="datetime-local"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
            />
            <span className="gbp__hint">Leave this empty to save it as a draft.</span>
          </label>

          {formError && <p className="gbp__error">{formError}</p>}

          <div className="gbp__actions">
            <button
              type="button"
              className="btn btn--primary"
              disabled={create.isPending || uploading}
              onClick={() => submit(true)}
            >
              {create.isPending ? 'Scheduling...' : uploading ? 'Adding photo...' : 'Schedule it'}
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              disabled={create.isPending || uploading}
              onClick={() => submit(false)}
            >
              Save as draft
            </button>
          </div>
        </section>

        {/* ---------------- Queue ----------------
            Green, matching the composer and the GROW section. */}
        <section className="gbp__card hue-green sec-topcap">
          <h2>Your posts</h2>

          {isLoading && <p className="gbp__empty">Loading...</p>}
          {error && <p className="gbp__error">{error.message}</p>}
          {!isLoading && !error && posts.length === 0 && (
            <p className="gbp__empty">
              Nothing scheduled yet. Write your first post and it'll show up here.
            </p>
          )}

          {posts.length > 0 && (
            <ul className="gbp__list">
              {posts.map((p) => (
                <li key={p.id} className={`gbp__item is-${p.status}`}>
                  {Array.isArray(p.design?.photo_urls) && p.design.photo_urls[0] && (
                    <img
                      className="gbp__thumb"
                      src={p.design.photo_urls[0]}
                      alt=""
                      loading="lazy"
                    />
                  )}
                  <div className="gbp__main">
                    <span className="gbp__type">
                      {GBP_POST_TYPE_LABELS[p.post_type] || p.post_type}
                    </span>
                    <p className="gbp__caption">{p.caption}</p>
                    <span className="gbp__when">{statusLabel(p)}</span>
                    {/* Honest failure surfacing -- never a silent disappearance. */}
                    {p.status === 'failed' && p.last_error && (
                      <span className="gbp__failreason">{p.last_error}</span>
                    )}
                  </div>

                  <div className="gbp__itemactions">
                    {p.external_post_url && (
                      <a
                        className="gbp__btn"
                        href={p.external_post_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View <Icon d={ExternalIcon} />
                      </a>
                    )}
                    {/* A published post is deliberately not editable -- the copy
                        on Google would stop matching what's shown here. */}
                    {p.status !== 'posted' && p.status !== 'publishing' && (
                      <>
                        <button
                          type="button"
                          className="gbp__btn"
                          disabled={publishNow.isPending}
                          onClick={() => publishNow.mutate(p.id)}
                        >
                          Post now
                        </button>
                        {p.status === 'scheduled' && (
                          <button
                            type="button"
                            className="gbp__btn"
                            onClick={() =>
                              update.mutate({ id: p.id, patch: { scheduled_at: null } })
                            }
                          >
                            Unschedule
                          </button>
                        )}
                        <button
                          type="button"
                          className="gbp__btn gbp__btn--danger"
                          disabled={remove.isPending}
                          onClick={() => remove.mutate(p.id)}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
